import React, { useEffect, useState } from "react";
import { WasteDetailsTableProps } from "./types";
import { Chip, Grid, Tab } from "@material-ui/core";
import DetailsTable from "../DetailsTable";
import useStyles from "./styles";
import { TabContext, TabList, TabPanel } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { ProductWasteStatisticsState } from "../../../../store/statistics/productwaste/types";
import { thunkFetchProductWasteStatistics } from "../../../../store/statistics/productwaste/actions";
import StatTable from "../../components/StatTable";
import { Currency } from "../../../../components/misc/Currency";
import { useI18n } from "../../../../hooks/useI18n";

const WasteDetailsTable = ({
  wastePerReason,
  wastePerCategory,
  totalWaste,
  productWaste
}: WasteDetailsTableProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useI18n();

  const [selectedTab, setSelectedTab] = useState("1");
  const [filters, setFilters] = useState([
    { label: "Damaged", filter: "Package has been damaged", active: false },
    {
      label: t("common.messages.durabilityPassed"),
      filter: "Durability date has passed",
      active: false
    },
    { label: "Other", filter: "Other", active: false },
    { label: "Remove from Store", filter: "Remove from Store", active: false },
    {
      label: t("common.formFields.shortDate"),
      filter: t("common.formFields.shortDurabilityDate"),
      active: false
    },
    { label: "Stolen", filter: "Product has been stolen", active: false }
  ]);
  const productWasteStatisticsState = useSelector<
    AppState,
    ProductWasteStatisticsState
  >(s => s.productWasteStatistics);

  let { productWasteStatistics } = productWasteStatisticsState;

  useEffect(() => {
    if (productWaste) {
      dispatch(
        thunkFetchProductWasteStatistics(
          productWaste.periodFrom,
          productWaste.periodTo,
          productWaste.storeId
        )
      );
    }
  }, [productWaste]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setSelectedTab(newValue);
  };

  const populateProductTableData = (): any[] => {
    if (selectedTab != "3") return [];

    const filter = filters.reduce((a, f) => {
      if (f.active) {
        a = `${a},${f.filter}`;
      }
      return a;
    }, "");

    const filteredWaste =
      filter !== ""
        ? productWasteStatistics.filter(p => filter.includes(p.reason))
        : productWasteStatistics;

    return filteredWaste.map(p => ({
      name: p.name,
      reason: p.reason,
      costFormatted: <Currency value={p.cost} />,
      cost: p.cost,
      ofTotalWaste: `${((p.cost * 100) / totalWaste).toFixed(2)}%`
    }));
  };

  const toggleFilter =
    (chip: any): any =>
    () => {
      const chipFilter = filters.map(f => {
        if (chip.label === f.label) {
          f.active = !f.active;
        }
        return f;
      });

      setFilters(chipFilter);
    };

  return (
    <TabContext value={selectedTab}>
      <TabList
        onChange={handleChange}
        className={classes.tabHeader}
        TabIndicatorProps={{ className: classes.indicator }}
      >
        <Tab
          label={"Waste by spoilage reason"}
          value="1"
          className={classes.tabEntry}
        />
        <Tab
          label={"Waste by product category"}
          value="2"
          className={classes.tabEntry}
        />
        <Tab
          label={"Waste by product"}
          value="3"
          className={classes.tabEntry}
        />
      </TabList>

      <TabPanel value="1" className={classes.tabPanel}>
        <DetailsTable
          key={"wastePerReason"}
          detailsMap={wastePerReason}
          headers={[
            t("common.tableHeaders.spoilageReason"),
            t("common.tableHeaders.cost")
          ]}
          percentage={{
            showPercentage: true,
            total: totalWaste,
            title: "Of total waste"
          }}
        />
      </TabPanel>
      <TabPanel value="2" className={classes.tabPanel}>
        <DetailsTable
          key={"wastePerCategory"}
          detailsMap={
            totalWaste > 0 ? wastePerCategory : new Map<string, number>()
          }
          headers={[
            t("common.tableHeaders.productCategory"),
            t("common.tableHeaders.cost")
          ]}
          percentage={{
            showPercentage: true,
            total: totalWaste,
            title: "Of total waste"
          }}
        />
      </TabPanel>
      <TabPanel value="3" className={classes.tabPanel}>
        <Grid
          className={classes.topHeader}
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
        >
          {filters.map(f => (
            <Grid item key={`${f.label}grid`}>
              <Chip
                key={f.label}
                label={f.label}
                size={"small"}
                className={f.active ? classes.chipActive : classes.chip}
                onClick={toggleFilter(f)}
              />
            </Grid>
          ))}
        </Grid>
        <StatTable
          exportName={"Waste by product statistics"}
          isLoading={false}
          columns={[
            { title: "Product", field: "name", cellStyle: { padding: "8px" } },
            {
              title: t("common.tableHeaders.spoilageReason"),
              field: "reason",
              cellStyle: { padding: "8px" }
            },
            {
              title: "Cost",
              field: "costFormatted",
              cellStyle: { padding: "8px" },
              customSort: (a: any, b: any) => {
                return a.cost - b.cost;
              }
            },
            {
              title: "Of total waste",
              field: "ofTotalWaste",
              cellStyle: { padding: "8px" },
              customSort: (a: any, b: any) => {
                let x = a.ofTotalWaste.slice(0, -1);
                let y = b.ofTotalWaste.slice(0, -1);
                return parseInt(x) - parseInt(y);
              }
            }
          ]}
          data={populateProductTableData()}
          noSearch
          dense
        />
      </TabPanel>
    </TabContext>
  );
};
export default WasteDetailsTable;
